/**
 * @file 회의록 게시판 — 주차 대시보드
 *  주차를 고르면 부서별 카드로 그 주 회의록을 한눈에 (미작성 부서 포함).
 * 부서별·주간(월요일 시작)이되, 회의록은 '정확한 회의 날짜(meetingDate)' 단위로 관리한다.
 * 한 부서-한 주차에 여러 날짜의 회의록이 존재할 수 있고, 같은 (부서, 회의 날짜)로 저장하면 기존 건이 수정된다.
 * 조회는 누구나, 작성·수정·삭제는 본인 부서만.
 * 저장마다 버전 스냅샷이 쌓여 '수정 이력'에서 버전별 내용을 열람할 수 있다.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box, Chip, Collapse, Divider, IconButton, Paper, Stack, TextField, Typography,
} from "@mui/material";
import dayjs from "dayjs";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import BeachAccessRoundedIcon from "@mui/icons-material/BeachAccessRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";

import FormModal from "../../component/common/FormModal";
import FormField from "../../component/common/FormField";
import ActionButton from "../../component/common/ActionButton";
import LoadingDots from "../../component/common/LoadingDots";
import AttachmentPreviewList from "../../component/common/AttachmentPreviewList";
import EmployeeAutocomplete from "../../component/common/EmployeeAutocomplete";
import { meetingMinutesApi } from "./api/meetingMinutesApi";
import { runApiAction } from "../../util/apiFeedback";
import { notify } from "../../util/toast";
import { confirm as confirmDialog } from "../../util/confirmDialog";
import { formatDottedDate, formatDottedDateTimeMinute } from "../../util/dateUtils";
import { ds } from "../../styles/designSystem";

const C = ds.colors;

/** 주어진 날짜가 속한 주의 월요일 (dayjs, ko 로케일은 일요일 시작이라 수동 계산). */
const mondayOf = (d) => {
    const x = dayjs(d);
    const dow = x.day();                 // 0=일 … 6=토
    const diff = dow === 0 ? -6 : 1 - dow;
    return x.add(diff, "day");
};
/** 주어진 날짜가 속한 주의 일요일 (월요일 + 6일). */
const sundayOf = (d) => mondayOf(d).add(6, "day");
const todayMonday = () => mondayOf(dayjs()).format("YYYY-MM-DD");

/** 작성 가능 최대 주차(월요일) = 다음 주. 과거는 무제한, 미래는 다음 주까지만. */
const maxWritableWeek = () => mondayOf(dayjs().add(1, "week")).format("YYYY-MM-DD");
/** 작성 가능 최대 회의 날짜 = 다음 주 일요일. */
const maxWritableDate = () => sundayOf(dayjs().add(1, "week")).format("YYYY-MM-DD");
/** 그 주(월요일 정규화)가 작성 가능 범위 안인지 (ISO 문자열 비교). */
const isWeekWritable = (weekStart) =>
    Boolean(weekStart) && mondayOf(weekStart).format("YYYY-MM-DD") <= maxWritableWeek();

/** "2026.07.06 ~ 07.12" — weekEnd 없으면 월요일+6일. */
const weekRangeLabel = (weekStart, weekEnd) => {
    if (!weekStart) return "";
    const s = dayjs(weekStart);
    const e = weekEnd ? dayjs(weekEnd) : s.add(6, "day");
    return `${s.format("YYYY.MM.DD")} ~ ${e.format("MM.DD")}`;
};
/** 주차의 일요일(끝) — rec.weekEnd 우선, 없으면 월요일+6일. */
const weekEndOf = (weekStart, weekEnd) =>
    weekEnd || (weekStart ? dayjs(weekStart).add(6, "day").format("YYYY-MM-DD") : null);

/** 회의록의 실제 회의 날짜 — meetingDate 가 없는 레거시 행은 weekStart 로 폴백. */
const recDate = (rec) => rec?.meetingDate || rec?.weekStart || null;
/** "2026.07.15(수)" — 상세·이력용 정확한 회의 날짜. */
const meetingDateLabel = (d) => (d ? dayjs(d).format("YYYY.MM.DD(ddd)") : "");
/** "07.15(수)" — 카드 목록의 날짜 배지. */
const dateBadgeLabel = (d) => (d ? dayjs(d).format("MM.DD(ddd)") : "-");
/** 참석자 수 — attendeeCount 우선, 없으면 attendees 길이. */
const attendeeCountOf = (rec) => rec?.attendeeCount ?? (rec?.attendees || []).length;
/** 회의 날짜 오름차순(같으면 id 오름차순) 비교자. */
const byMeetingDateAsc = (a, b) => {
    const da = recDate(a) || "";
    const db = recDate(b) || "";
    if (da !== db) return da < db ? -1 : 1;
    return (a?.id ?? 0) - (b?.id ?? 0);
};

/** 서버 참석자({employeeId,name,deptName,positionName}) → EmployeeAutocomplete 값 형태. */
const toAttendeeOption = (a) => ({
    id: a.employeeId, name: a.name, department: a.deptName, position: a.positionName,
});
/** 참석자 배열 중복 제거 (id 기준, 선택 순서 유지). */
const dedupeAttendees = (list) => {
    const seen = new Set();
    const out = [];
    (list || []).forEach((a) => {
        if (!a || typeof a === "string" || a.id == null || seen.has(a.id)) return;
        seen.add(a.id);
        out.push(a);
    });
    return out;
};

/** 휴가 기간 라벨: 하루면 "7/8(수)", 여러날이면 "7/8 ~ 7/10". */
const leaveDateLabel = (s, e) => {
    if (!s) return "";
    const sd = dayjs(s);
    if (!e || e === s) return sd.format("M/D(ddd)");
    return `${sd.format("M/D")} ~ ${dayjs(e).format("M/D")}`;
};

/** 휴가 유형별 색상 (ds 토큰). */
const leaveColor = (t) => {
    if (t === "출장") return { bg: C.primaryBg, fg: C.primaryText };
    if ((t || "").startsWith("반차")) return { bg: C.warningBg, fg: C.warning };
    if (t === "병가") return { bg: C.errorBg, fg: C.error };
    if (t === "대체휴무") return { bg: C.successBg, fg: C.success };
    return { bg: C.borderLight, fg: C.textSecondary };   // 연차·시간연차·기타
};

export default function MeetingMinutesPage() {
    const [loading, setLoading] = useState(true);
    const [ready, setReady] = useState(false);
    const [me, setMe] = useState(null);
    const [departments, setDepartments] = useState([]);

    const [weekAnchor, setWeekAnchor] = useState(todayMonday());
    const [rows, setRows] = useState([]);

    // 상세 모달
    const [detail, setDetail] = useState(null);
    const [revisions, setRevisions] = useState([]);
    const [revLoading, setRevLoading] = useState(false);
    const [expandedRev, setExpandedRev] = useState(null);
    const [detailLeaves, setDetailLeaves] = useState([]);
    const [detailLeavesLoading, setDetailLeavesLoading] = useState(false);
    const [detailAttachments, setDetailAttachments] = useState([]);
    const fileInputRef = useRef(null);

    // 주차 대시보드: 그 주 부서별 휴가·부재 (deptName → [leave])
    const [weekLeavesByDept, setWeekLeavesByDept] = useState(() => new Map());
    // 카드별 "+n건 더" 펼침 상태 (카드 key 집합)
    const [expandedCards, setExpandedCards] = useState(() => new Set());

    // 작성/수정 폼 모달
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deptAdding, setDeptAdding] = useState(false);
    const employeesRef = useRef(null);   // 부서원 일괄 추가용 전 직원 목록 캐시

    const canPost = Boolean(me && me.deptId);
    const canEditRec = (rec) => Boolean(me && rec && rec.deptId != null && me.deptId === rec.deptId);
    const isThisWeek = weekAnchor === todayMonday();

    // ─── 로딩 ────────────────────────────────────────────────────
    const loadMasters = useCallback(() => {
        Promise.allSettled([meetingMinutesApi.getMe(), meetingMinutesApi.getDepartments()])
            .then(([m, d]) => {
                const meVal = m.status === "fulfilled" ? m.value : null;
                setMe(meVal);
                if (d.status === "fulfilled") setDepartments(d.value || []);
                setReady(true);
            });
    }, []);

    const reloadRows = useCallback(() => {
        setLoading(true);
        setExpandedCards(new Set());
        meetingMinutesApi.getList({ week: weekAnchor })
            .then((d) => setRows(d || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [weekAnchor]);

    useEffect(() => { loadMasters(); }, [loadMasters]);
    useEffect(() => { if (ready) reloadRows(); }, [ready, reloadRows]);

    // 주차 대시보드용: 그 주 전 부서 휴가·부재를 한 번에 불러와 부서명으로 그룹
    useEffect(() => {
        if (!ready) return;
        const to = weekEndOf(weekAnchor);
        meetingMinutesApi.getLeaves(null, weekAnchor, to)
            .then((list) => {
                const map = new Map();
                (list || []).forEach((l) => {
                    const key = l.deptName || "";
                    if (!map.has(key)) map.set(key, []);
                    map.get(key).push(l);
                });
                setWeekLeavesByDept(map);
            })
            .catch(() => setWeekLeavesByDept(new Map()));
    }, [ready, weekAnchor]);

    // ─── 상세 ────────────────────────────────────────────────────
    const loadDetailLeaves = (rec) => {
        if (!rec || rec.deptId == null) { setDetailLeaves([]); return; }
        setDetailLeavesLoading(true);
        meetingMinutesApi.getLeaves(rec.deptId, rec.weekStart, weekEndOf(rec.weekStart, rec.weekEnd))
            .then((d) => setDetailLeaves(d || []))
            .catch(() => setDetailLeaves([]))
            .finally(() => setDetailLeavesLoading(false));
    };

    const loadDetailAttachments = (id) => {
        meetingMinutesApi.getAttachments(id)
            .then((d) => setDetailAttachments(d || []))
            .catch(() => setDetailAttachments([]));
    };

    const openDetail = (rec) => {
        setDetail(rec);
        setExpandedRev(null);
        setRevisions([]);
        setRevLoading(true);
        setDetailAttachments([]);
        meetingMinutesApi.getRevisions(rec.id)
            .then((d) => setRevisions(d || []))
            .catch(() => setRevisions([]))
            .finally(() => setRevLoading(false));
        loadDetailLeaves(rec);
        loadDetailAttachments(rec.id);
    };

    const reloadDetail = (id) => {
        meetingMinutesApi.get(id).then((rec) => setDetail(rec)).catch(() => {});
        setRevLoading(true);
        meetingMinutesApi.getRevisions(id)
            .then((d) => setRevisions(d || []))
            .catch(() => setRevisions([]))
            .finally(() => setRevLoading(false));
        loadDetailAttachments(id);
    };

    // ─── 작성/수정 폼 ────────────────────────────────────────────
    /** 작성 폼의 회의 날짜 기본값·선택 범위 — 주차 대시보드에서 들어오면 그 주(월~일)로 제한. */
    const dateBoundsForNew = (week, lockWeek) => {
        const wStart = mondayOf(week || dayjs()).format("YYYY-MM-DD");
        const wEnd = sundayOf(week || dayjs()).format("YYYY-MM-DD");
        const maxAll = maxWritableDate();
        const today = dayjs().format("YYYY-MM-DD");
        if (!lockWeek) return { min: undefined, max: maxAll, value: today <= maxAll ? today : maxAll };
        const max = wEnd < maxAll ? wEnd : maxAll;
        // 그 주에 오늘이 속하면 오늘, 아니면 그 주 월요일
        const value = today >= wStart && today <= max ? today : wStart;
        return { min: wStart, max, value };
    };

    const openNew = (week, lockWeek = false) => {
        const b = dateBoundsForNew(week, lockWeek);
        setForm({
            id: null,
            deptId: me?.deptId ?? "",
            deptName: me?.deptName ?? "",
            meetingDate: b.value,
            dateMin: b.min,
            dateMax: b.max,
            location: "",
            // 본인은 기본 참석자로 포함
            attendees: me?.id != null
                ? [{ id: me.id, name: me.name, department: me.deptName, position: me.positionName }]
                : [],
            workContent: "",
            specialNotes: "",
            lockWeek,   // 특정 주차(대시보드)에서 들어오면 그 주차 안에서만 날짜 선택
            pendingFiles: [],   // 저장 후 업로드할 새 파일(File[])
            attachments: [],    // 기존 첨부(수정 모드)
        });
    };

    const openEdit = (rec) => {
        setForm({
            id: rec.id,
            deptId: rec.deptId,
            deptName: rec.deptName,
            meetingDate: recDate(rec) || dayjs().format("YYYY-MM-DD"),
            dateMin: undefined,
            dateMax: maxWritableDate(),
            location: rec.location || "",
            attendees: dedupeAttendees((rec.attendees || []).map(toAttendeeOption)),
            workContent: rec.workContent || "",
            specialNotes: rec.specialNotes || "",
            lockWeek: false,
            pendingFiles: [],
            attachments: [],
        });
        meetingMinutesApi.getAttachments(rec.id)
            .then((d) => setForm((f) => (f && f.id === rec.id ? { ...f, attachments: d || [] } : f)))
            .catch(() => {});
    };

    /** 우리 부서 전원을 참석자로 한 번에 채운다 (중복 제거). */
    const addMyDeptMembers = async () => {
        const deptId = form?.deptId;
        if (deptId == null || deptId === "") return;
        setDeptAdding(true);
        try {
            if (!employeesRef.current) {
                employeesRef.current = (await meetingMinutesApi.getEmployees()) || [];
            }
            const members = employeesRef.current
                .filter((e) => e && e.deptId != null && Number(e.deptId) === Number(deptId))
                .map((e) => ({ id: e.id, name: e.name, department: e.deptName, position: e.positionName }));
            if (members.length === 0) {
                notify("부서원 정보를 찾지 못했습니다.", "warning");
                return;
            }
            setForm((f) => (f ? { ...f, attendees: dedupeAttendees([...(f.attendees || []), ...members]) } : f));
        } catch (e) {
            notify(e?.message || "부서원 목록을 불러오지 못했습니다.", "error");
        } finally {
            setDeptAdding(false);
        }
    };

    /** 기존 첨부(수정 모드) 즉시 삭제 — 본인 부서만. */
    const removeExistingAttachment = async (attId) => {
        if (!(await confirmDialog({ message: "이 첨부를 삭제하시겠습니까?" }))) return;
        await runApiAction({
            request: () => meetingMinutesApi.removeAttachment(attId),
            successMsg: "첨부가 삭제되었습니다.",
            onSuccess: () => {
                setForm((f) => (f ? { ...f, attachments: (f.attachments || []).filter((a) => a.id !== attId) } : f));
                setDetailAttachments((prev) => prev.filter((a) => a.id !== attId));
            },
        });
    };

    /** 신규 작성 폼과 같은 (부서, 회의 날짜)인 기존 회의록 — 있으면 서버 POST 가 그 건을 덮어쓴다. */
    const findSameDateRec = (f) =>
        (f.id ? null : rows.find(
            (r) => Number(r.deptId) === Number(f.deptId) && recDate(r) === f.meetingDate
        )) || null;

    const saveForm = async () => {
        const f = form;
        if (!f.workContent.trim() || !f.meetingDate || !f.deptId) return;

        // 신규 저장이라도 같은 (부서, 회의 날짜) 건이 있으면 서버가 그 건을 수정(upsert)한다.
        // 그 주 회의록은 이미 rows 에 있으므로 저장 전에 잡아 사용자에게 확인받는다.
        const dup = findSameDateRec(f);
        if (dup) {
            const ok = await confirmDialog({
                title: "같은 날짜의 회의록이 이미 있습니다",
                message:
                    `${meetingDateLabel(f.meetingDate)}에 ${dup.createdByName || "다른 사용자"}님이 작성한 회의록이 있습니다.\n` +
                    "이대로 저장하면 그 회의록의 업무 내용·참석자가 지금 입력한 값으로 대체됩니다.\n" +
                    "(이전 내용은 수정 이력에 남습니다)\n\n" +
                    "계속 저장하시겠습니까? 취소하면 기존 회의록 수정 화면으로 이동합니다.",
                confirmLabel: "계속 저장",
                cancelLabel: "기존 회의록 수정",
                variant: "danger",
            });
            if (!ok) { openEdit(dup); return; }
        }

        const loc = (f.location || "").trim();
        const notes = (f.specialNotes || "").trim();
        // 신규 POST 는 upsert 로 기존 건을 수정할 수 있으므로, 입력하지 않은 선택 필드는
        // 빈 문자열 대신 아예 보내지 않는다(서버 계약: null = 기존값 유지).
        const isNew = !f.id;
        const payload = {
            deptId: f.deptId,
            meetingDate: f.meetingDate,
            location: isNew && !loc ? undefined : loc,
            workContent: f.workContent,
            specialNotes: isNew && !notes ? undefined : notes,
            attendeeIds: (f.attendees || []).map((a) => a?.id).filter((v) => v != null),
        };
        const res = await runApiAction({
            request: () => f.id
                ? meetingMinutesApi.update(f.id, payload)
                : meetingMinutesApi.create(payload),
            // 신규 저장이라도 같은 (부서, 회의 날짜) 건이 있으면 서버가 그 건을 수정한다.
            successMsg: f.id
                ? "회의록이 수정되었습니다."
                : (d) => (d?.currentVersion > 1
                    ? "같은 날짜의 기존 회의록이 수정되었습니다. (수정 이력에서 이전 내용을 볼 수 있습니다)"
                    : "회의록이 저장되었습니다."),
            setLoading: setSaving,
            // 날짜 충돌 등 서버 에러는 toast 로만 알리고 폼은 열어 둔다(throw 하면 미처리 rejection).
        });
        if (res.ok) {
            const savedId = f.id || res.data?.id || res.data?.data?.id;
            // 새로 추가한 파일 업로드(생성/수정 후). 실패해도 본문 저장은 유지.
            if (savedId && (f.pendingFiles || []).length) {
                setSaving(true);
                try {
                    for (const file of f.pendingFiles) {
                        try { await meetingMinutesApi.uploadAttachment(savedId, file); }
                        catch (e) { notify(e?.message || `${file.name} 업로드에 실패했습니다.`, "error"); }
                    }
                } finally { setSaving(false); }
            }
            setForm(null);
            reloadRows();
            if (savedId && detail && detail.id === savedId) reloadDetail(savedId);
        }
    };

    const deleteRec = async (rec) => {
        if (!(await confirmDialog({ message: "이 회의록을 삭제하시겠습니까? 수정 이력도 함께 삭제됩니다." }))) return;
        const res = await runApiAction({
            request: () => meetingMinutesApi.remove(rec.id),
            successMsg: "회의록이 삭제되었습니다.",
            setLoading: setSaving,
        });
        if (res.ok) {
            setForm(null);
            setDetail(null);
            reloadRows();
        }
    };

    // ─── 파생 데이터 ─────────────────────────────────────────────
    // 주차 대시보드용: 부서 → 그 주 회의록 목록(여러 날짜 가능, 날짜 오름차순)
    const { deptCards, orphanCards } = useMemo(() => {
        const byDept = new Map();
        rows.forEach((r) => {
            const key = r.deptId != null ? String(r.deptId) : "none";
            if (!byDept.has(key)) byDept.set(key, []);
            byDept.get(key).push(r);
        });
        byDept.forEach((list) => list.sort(byMeetingDateAsc));

        const known = new Set(departments.map((d) => String(d.deptId)));
        const cards = departments.map((d) => ({
            key: `d${d.deptId}`,
            deptId: d.deptId,
            deptName: d.deptName,
            recs: byDept.get(String(d.deptId)) || [],
        }));
        // 부서 목록에 없는(또는 부서 없는) 회의록도 카드로 묶어서 노출
        const orphans = [];
        byDept.forEach((list, key) => {
            if (known.has(key)) return;
            orphans.push({
                key: `o${key}`,
                deptId: list[0]?.deptId ?? null,
                deptName: list[0]?.deptName || "부서 없음",
                recs: list,
            });
        });
        return { deptCards: cards, orphanCards: orphans };
    }, [rows, departments]);

    // 주차 대시보드 상단: 부서별 작성 현황 요약 (1건 이상 작성한 부서 수 기준)
    const weekSummary = useMemo(() => {
        const total = deptCards.length;
        const written = deptCards.filter((c) => c.recs.length > 0).length;
        const missingDepts = deptCards.filter((c) => c.recs.length === 0);
        const pct = total ? Math.round((written / total) * 100) : 0;
        return { total, written, missing: total - written, missingDepts, pct };
    }, [deptCards]);

    const gotoWeek = (deltaDays) => setWeekAnchor(dayjs(weekAnchor).add(deltaDays, "day").format("YYYY-MM-DD"));

    const toggleCardExpand = (key) => setExpandedCards((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    // ─── 카드 (주차 대시보드) ────────────────────────────────────
    /** 카드에 기본으로 펼쳐 보여줄 회의록 수 — 넘치면 "+n건 더". */
    const CARD_VISIBLE_RECS = 3;

    /** 카드 안의 회의록 한 줄 — 날짜·장소·참석자 수·본문 미리보기. 클릭하면 상세. */
    const renderRecRow = (rec) => (
        <Box
            key={rec.id}
            onClick={() => openDetail(rec)}
            sx={{
                border: `1px solid ${C.borderLight}`, borderRadius: 1, px: 1.25, py: 0.85, cursor: "pointer",
                transition: "border-color .15s, background-color .15s",
                "&:hover": { borderColor: C.primaryBorder, bgcolor: C.rowHover },
            }}
        >
            <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.4 }}>
                <Chip size="small" label={dateBadgeLabel(recDate(rec))}
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: C.primaryBg, color: C.primaryText, flexShrink: 0 }} />
                {rec.location && (
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
                        <PlaceRoundedIcon sx={{ fontSize: 13, color: C.textMuted, flexShrink: 0 }} />
                        <Typography noWrap sx={{ fontSize: 11.5, color: C.textSecondary }} title={rec.location}>
                            {rec.location}
                        </Typography>
                    </Stack>
                )}
                <Box sx={{ flex: 1, minWidth: 4 }} />
                {attendeeCountOf(rec) > 0 && (
                    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }} title="참석자">
                        <GroupRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                        <Typography sx={{ fontSize: 11.5, color: C.textMuted, fontWeight: 600 }}>
                            {attendeeCountOf(rec)}
                        </Typography>
                    </Stack>
                )}
                {rec.edited && (
                    <Chip size="small" label={`수정 ${rec.currentVersion - 1}`}
                        sx={{ height: 18, fontSize: 10.5, bgcolor: C.borderLight, color: C.textSecondary, flexShrink: 0 }} />
                )}
            </Stack>
            <Typography sx={{
                ...ds.typography.body, fontSize: 12.5, color: C.textSecondary, whiteSpace: "pre-wrap",
                display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
                {rec.workContent}
            </Typography>
            <Typography noWrap sx={{ fontSize: 11, color: C.textMuted, mt: 0.4 }}>
                {rec.createdByName || "-"} · {formatDottedDate(rec.updatedAt)}
                {rec.specialNotes ? " · 특이사항" : ""}
            </Typography>
        </Box>
    );

    const renderCard = ({ key, deptId, deptName, recs }) => {
        const isMine = Boolean(me && deptId != null && me.deptId === deptId);
        const deptLeaves = weekLeavesByDept.get(deptName) || [];
        const hasRecs = recs.length > 0;
        const expanded = expandedCards.has(key);
        const visible = expanded ? recs : recs.slice(0, CARD_VISIBLE_RECS);
        const restCount = recs.length - visible.length;
        const writable = isWeekWritable(weekAnchor);

        return (
            <Paper
                key={key}
                sx={{
                    ...ds.cardPadded, p: 2, display: "flex", flexDirection: "column", gap: 1, minHeight: 200,
                    border: hasRecs ? `1.5px solid ${C.border}` : `1.5px dashed ${C.border}`,
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ ...ds.typography.sectionTitle, fontSize: 14 }}>
                            {deptName || "부서 없음"}
                        </Typography>
                        {isMine && (
                            <Chip size="small" label="우리 부서"
                                sx={{ height: 18, fontSize: 10, bgcolor: C.primaryBg, color: C.primaryText }} />
                        )}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        {hasRecs && (
                            <Chip size="small" label={`${recs.length}건`}
                                sx={{ height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: C.successBg, color: C.success }} />
                        )}
                    </Stack>
                </Stack>

                {hasRecs ? (
                    <>
                        <Stack spacing={0.75} sx={{
                            flex: 1,
                            // 펼쳤을 때 카드가 무한히 길어지지 않도록 높이 제한 + 스크롤
                            ...(expanded && recs.length > CARD_VISIBLE_RECS
                                ? { maxHeight: 340, overflowY: "auto", pr: 0.25 }
                                : {}),
                        }}>
                            {visible.map(renderRecRow)}
                        </Stack>
                        {recs.length > CARD_VISIBLE_RECS && (
                            <Typography
                                onClick={() => toggleCardExpand(key)}
                                sx={{
                                    fontSize: 11.5, fontWeight: 600, color: C.primary, cursor: "pointer",
                                    textAlign: "center", py: 0.25, "&:hover": { textDecoration: "underline" },
                                }}
                            >
                                {expanded ? "접기" : `+${restCount}건 더 보기`}
                            </Typography>
                        )}
                    </>
                ) : (
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>아직 작성되지 않았습니다</Typography>
                    </Box>
                )}

                {/* 본인 부서면 이미 작성한 회의록이 있어도 다른 날짜 회의록을 계속 추가할 수 있다. */}
                {isMine && (
                    <ActionButton variant="add" label={hasRecs ? "회의록 추가" : "작성하기"}
                        onClick={() => openNew(weekAnchor, true)}
                        disabled={!writable}
                        tooltip={!writable ? "회의록은 다음 주까지만 작성할 수 있습니다." : undefined}
                        sx={{ fontSize: 11.5, alignSelf: "flex-start" }} />
                )}
            </Paper>
        );
    };

    // ─── 렌더 ────────────────────────────────────────────────────
    return (
        <Box sx={{ maxWidth: 1280, mx: "auto", py: 1 }}>
            {/* 1행: 제목 + 작성 */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={ds.typography.pageTitle}>회의록</Typography>
                </Stack>
                <ActionButton
                    variant="add"
                    onClick={() => openNew(weekAnchor, true)}
                    label="회의록 작성"
                    disabled={!canPost || !isWeekWritable(weekAnchor)}
                    tooltip={
                        !canPost ? "소속 부서가 있어야 작성할 수 있습니다."
                            : !isWeekWritable(weekAnchor) ? "회의록은 다음 주까지만 작성할 수 있습니다."
                                : undefined
                    }
                />
            </Stack>
            <Typography sx={{ ...ds.typography.pageSubtitle, mb: 2 }}>
                부서별 회의록 (주 단위 조회 · 월요일 시작) · 회의 날짜별로 저장되며 같은 날짜에 다시 저장하면 수정 이력이 쌓입니다
            </Typography>

            {/* 주차 네비게이터 */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 2 }}>
                {!isThisWeek && (
                    <ActionButton variant="secondary" label="이번 주"
                                  onClick={() => setWeekAnchor(todayMonday())} sx={{ fontSize: 11.5, mr: 0.5 }} />
                )}
                <IconButton size="small" onClick={() => gotoWeek(-7)}><ChevronLeftRoundedIcon fontSize="small" /></IconButton>
                <Box sx={{ px: 1.5, py: 0.5, minWidth: 200, textAlign: "center", border: `1px solid ${C.border}`, borderRadius: 1, bgcolor: C.surface }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                        {weekRangeLabel(weekAnchor)}
                    </Typography>
                </Box>
                <IconButton size="small" onClick={() => gotoWeek(7)}><ChevronRightRoundedIcon fontSize="small" /></IconButton>
            </Stack>

            {/* 본문 */}
            {loading ? (
                <Box sx={{ py: 8, textAlign: "center" }}><LoadingDots /></Box>
            ) : (
                /* ── 주차 대시보드 ── */
                departments.length === 0 && orphanCards.length === 0 ? (
                    <Paper sx={{ ...ds.cardPadded, textAlign: "center", py: 6 }}>
                        <Typography sx={{ ...ds.typography.body, color: C.textMuted }}>부서 정보가 없습니다.</Typography>
                    </Paper>
                ) : (
                    <>
                        {weekSummary.total > 0 && (
                            <Paper sx={{ ...ds.cardPadded, p: 2, mb: 2 }}>
                                <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap" useFlexGap>
                                    {/* 진행률 */}
                                    <Stack spacing={0.6} width={'100%'}>
                                        <Stack direction="row" alignItems="baseline" spacing={0.75}>
                                            <Typography sx={ds.typography.label}>작성 현황</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.textPrimary, lineHeight: 1 }}>
                                                {weekSummary.written}
                                                <Typography component="span" sx={{ fontSize: 14, fontWeight: 600, color: C.textMuted }}>
                                                    {` / ${weekSummary.total}개 부서`}
                                                </Typography>
                                            </Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: weekSummary.pct === 100 ? C.success : C.primary }}>
                                                {weekSummary.pct}%
                                            </Typography>
                                        </Stack>
                                        <Box sx={{ height: 8, borderRadius: 4, bgcolor: C.borderLight, overflow: "hidden" }}>
                                            <Box sx={{ height: "100%", width: `${weekSummary.pct}%`, bgcolor: weekSummary.pct === 100 ? C.success : C.primary, transition: "width .3s" }} />
                                        </Box>
                                    </Stack>
                                    {/* 완료/미작성 */}
                                    {/*<Stack direction="row" spacing={1} alignItems="center">*/}
                                    {/*    <Chip size="small" label={`작성 완료 ${weekSummary.written}`}*/}
                                    {/*        sx={{ bgcolor: C.successBg, color: C.success, fontWeight: 600 }} />*/}
                                    {/*    <Chip size="small" label={`미작성 ${weekSummary.missing}`} sx={{ bgcolor: weekSummary.missing ? C.warningBg : C.borderLight, color: weekSummary.missing ? C.warning : C.textMuted, fontWeight: 600 }} />*/}
                                    {/*</Stack>*/}
                                    {/* 미작성 부서 목록 */}
                                    {/*{weekSummary.missingDepts.length > 0 && (*/}
                                    {/*    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ flex: 1, minWidth: 0 }}>*/}
                                    {/*        <Typography sx={{ fontSize: 12, color: C.textMuted, flexShrink: 0 }}>미작성:</Typography>*/}
                                    {/*        {weekSummary.missingDepts.map((d) => (*/}
                                    {/*            <Chip key={d.deptId} size="small" variant="outlined" label={d.deptName}*/}
                                    {/*                sx={{ height: 22, fontSize: 11.5, borderColor: C.border, color: C.textSecondary }} />*/}
                                    {/*        ))}*/}
                                    {/*    </Stack>*/}
                                    {/*)}*/}
                                </Stack>
                            </Paper>
                        )}
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 2, alignItems: "start" }}>
                            {deptCards.map(renderCard)}
                            {orphanCards.map(renderCard)}
                        </Box>
                    </>
                )
            )}

            {/* 상세 모달 (읽기 전용 + 수정 이력) */}
            <FormModal
                open={Boolean(detail)}
                onClose={() => setDetail(null)}
                title={detail ? `${detail.deptName || "부서 없음"} 회의록` : ""}
                subtitle={detail
                    ? `${meetingDateLabel(recDate(detail))} · 주차 ${weekRangeLabel(detail.weekStart, detail.weekEnd)}`
                    : ""}
                maxWidth="md"
                footerExtras={detail && canEditRec(detail) ? (
                    <Stack direction="row" spacing={1}>
                        <ActionButton variant="edit" onClick={() => openEdit(detail)} label="수정" />
                        <ActionButton variant="delete" onClick={() => deleteRec(detail)} label="삭제" />
                    </Stack>
                ) : null}
            >
                {detail && (
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>
                                작성: {detail.createdByName || "-"} · {formatDottedDateTimeMinute(detail.createdAt)}
                            </Typography>
                            {detail.edited && (
                                <Typography sx={{ fontSize: 12.5, color: C.primary, fontWeight: 600 }}>
                                    (수정됨 · {detail.lastEditedByName || "-"} · {formatDottedDateTimeMinute(detail.updatedAt)})
                                </Typography>
                            )}
                        </Stack>
                        <Divider />
                        {/* 회의 개요 — 날짜·장소 */}
                        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                            <Box>
                                <Typography sx={{ ...ds.typography.label, mb: 0.5 }}>회의 날짜</Typography>
                                <Typography sx={{ ...ds.typography.body, fontWeight: 600 }}>
                                    {meetingDateLabel(recDate(detail)) || "-"}
                                </Typography>
                            </Box>
                            {detail.location && (
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ ...ds.typography.label, mb: 0.5 }}>장소</Typography>
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <PlaceRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                                        <Typography sx={ds.typography.body}>{detail.location}</Typography>
                                    </Stack>
                                </Box>
                            )}
                        </Stack>
                        {/* 참석자 */}
                        <Box>
                            <Typography sx={{ ...ds.typography.label, mb: 0.75 }}>
                                참석자 ({(detail.attendees || []).length})
                            </Typography>
                            {(detail.attendees || []).length === 0 ? (
                                <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>참석자 정보가 없습니다.</Typography>
                            ) : (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                    {detail.attendees.map((a) => (
                                        <Chip
                                            key={a.employeeId}
                                            size="small"
                                            label={a.name}
                                            title={[a.deptName, a.positionName].filter(Boolean).join(" · ") || a.name}
                                            sx={{ height: 22, fontSize: 11.5, fontWeight: 500, bgcolor: C.primaryBg, color: C.primaryText }}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </Box>
                        <Box>
                            <Typography sx={{ ...ds.typography.label, mb: 0.5 }}>업무 내용</Typography>
                            <Typography sx={{ ...ds.typography.body, whiteSpace: "pre-wrap", lineHeight: 1.65 }}>
                                {detail.workContent}
                            </Typography>
                        </Box>
                        {detail.specialNotes && (
                            <Box>
                                <Typography sx={{ ...ds.typography.label, mb: 0.5 }}>특이사항</Typography>
                                <Typography sx={{ ...ds.typography.body, whiteSpace: "pre-wrap", lineHeight: 1.65, color: C.textSecondary }}>
                                    {detail.specialNotes}
                                </Typography>
                            </Box>
                        )}
                        {detailAttachments.length > 0 && (
                            <Box>
                                <Typography sx={{ ...ds.typography.label, mb: 0.5 }}>첨부파일 ({detailAttachments.length})</Typography>
                                <AttachmentPreviewList attachments={detailAttachments} previewFn={meetingMinutesApi.previewAttachment} />
                            </Box>
                        )}
                        {/* 이번 주 휴가·부재 — 결재 승인분 자동 반영 */}
                        <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <Typography sx={ds.typography.label}>
                                    이번 주 휴가·부재{detailLeaves.length > 0 ? ` (${detailLeaves.length})` : ""}
                                </Typography>
                            </Stack>
                            {detailLeavesLoading ? (
                                <LoadingDots />
                            ) : detailLeaves.length === 0 ? (
                                <Typography sx={{ fontSize: 12.5, color: C.textMuted }}>이번 주 휴가·부재 내역이 없습니다.</Typography>
                            ) : (
                                <Stack spacing={0.5}>
                                    {detailLeaves.map((l) => {
                                        const col = leaveColor(l.leaveType);
                                        return (
                                            <Stack key={l.approvalId} direction="row" spacing={1} alignItems="center"
                                                sx={{ px: 1.25, py: 0.6, border: `1px solid ${C.borderLight}`, borderRadius: 1 }}>
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.textPrimary, minWidth: 64 }}>
                                                    {l.submitterName || "-"}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12.5, color: C.textSecondary, flex: 1 }}>
                                                    {leaveDateLabel(l.startDate, l.endDate)}
                                                </Typography>
                                                <Chip size="small" label={l.leaveType}
                                                    sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: col.bg, color: col.fg }} />
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                        <Divider />
                        {/* 수정 이력 */}
                        <Box>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                                <HistoryRoundedIcon sx={{ fontSize: 16, color: C.textMuted }} />
                                <Typography sx={ds.typography.label}>
                                    수정 이력{revisions.length > 0 ? ` (${revisions.length})` : ""}
                                </Typography>
                            </Stack>
                            {revLoading ? (
                                <LoadingDots />
                            ) : (
                                <Stack spacing={0.75}>
                                    {revisions.map((rev) => (
                                        <Box key={rev.id} sx={{ border: `1px solid ${C.borderLight}`, borderRadius: 1, overflow: "hidden" }}>
                                            <Stack
                                                direction="row" alignItems="center" spacing={1}
                                                onClick={() => setExpandedRev(expandedRev === rev.id ? null : rev.id)}
                                                sx={{ px: 1.5, py: 1, cursor: "pointer", "&:hover": { bgcolor: C.rowHover } }}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={rev.current ? `v${rev.version} 최신` : `v${rev.version}`}
                                                    sx={{
                                                        height: 20, fontSize: 11, fontWeight: 600,
                                                        bgcolor: rev.current ? C.primary : C.borderLight,
                                                        color: rev.current ? "#fff" : C.textSecondary,
                                                    }}
                                                />
                                                <Typography sx={{ fontSize: 12.5, color: C.textSecondary, flex: 1 }}>
                                                    {rev.editedByName || "-"}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                                                    {formatDottedDateTimeMinute(rev.editedAt)}
                                                </Typography>
                                                <ExpandMoreRoundedIcon sx={{
                                                    fontSize: 18, color: C.textMuted,
                                                    transform: expandedRev === rev.id ? "rotate(180deg)" : "none",
                                                    transition: "transform 0.15s",
                                                }} />
                                            </Stack>
                                            <Collapse in={expandedRev === rev.id}>
                                                <Box sx={{ px: 1.5, py: 1.25, borderTop: `1px solid ${C.borderLight}`, bgcolor: C.bg }}>
                                                    {/* 이 버전 시점의 회의 날짜·장소·참석자 스냅샷 */}
                                                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                                        <Stack direction="row" spacing={0.4} alignItems="center">
                                                            <Typography sx={ds.typography.caption}>회의 날짜</Typography>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSecondary }}>
                                                                {meetingDateLabel(rev.meetingDate) || "-"}
                                                            </Typography>
                                                        </Stack>
                                                        <Stack direction="row" spacing={0.4} alignItems="center" sx={{ minWidth: 0 }}>
                                                            <PlaceRoundedIcon sx={{ fontSize: 13, color: C.textMuted }} />
                                                            <Typography sx={{ fontSize: 12, color: C.textSecondary }}>
                                                                {rev.location || "장소 미입력"}
                                                            </Typography>
                                                        </Stack>
                                                    </Stack>
                                                    <Stack direction="row" spacing={0.4} sx={{ mb: 1.25 }}>
                                                        <GroupRoundedIcon sx={{ fontSize: 13, color: C.textMuted, mt: 0.2, flexShrink: 0 }} />
                                                        <Typography sx={{ fontSize: 12, color: C.textSecondary }}>
                                                            {rev.attendeeNames || "참석자 정보 없음"}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography sx={{ ...ds.typography.label, mb: 0.25 }}>업무 내용</Typography>
                                                    <Typography sx={{ ...ds.typography.body, whiteSpace: "pre-wrap", mb: rev.specialNotes ? 1.25 : 0 }}>
                                                        {rev.workContent}
                                                    </Typography>
                                                    {rev.specialNotes && (
                                                        <>
                                                            <Typography sx={{ ...ds.typography.label, mb: 0.25 }}>특이사항</Typography>
                                                            <Typography sx={{ ...ds.typography.body, whiteSpace: "pre-wrap", color: C.textSecondary }}>
                                                                {rev.specialNotes}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Stack>
                )}
            </FormModal>

            {/* 작성/수정 모달 */}
            <FormModal
                open={Boolean(form)}
                onClose={() => setForm(null)}
                title={form?.id ? "회의록 수정" : "회의록 작성"}
                maxWidth="md"
                onSave={saveForm}
                saving={saving}
                saveDisabled={!form?.workContent?.trim() || !form?.meetingDate || !form?.deptId}
                saveLabel={form?.id ? "수정" : "등록"}
            >
                {form && (
                    <Stack spacing={2}>
                        <FormField label="부서">
                            <TextField fullWidth size="small" value={form.deptName || "-"} disabled />
                        </FormField>
                        <FormField label="회의 날짜" required>
                            <TextField
                                fullWidth size="small" type="date"
                                value={form.meetingDate || ""}
                                onChange={(e) => setForm((f) => ({ ...f, meetingDate: e.target.value }))}
                                inputProps={{ min: form.dateMin, max: form.dateMax }}
                            />
                            {form.meetingDate && (() => {
                                const dup = findSameDateRec(form);
                                return (
                                    <Typography sx={{ fontSize: 12, color: dup ? C.error : C.textMuted, mt: 0.5 }}>
                                        이 회의록 주차: {weekRangeLabel(mondayOf(form.meetingDate).format("YYYY-MM-DD"))}
                                        {form.lockWeek ? " (선택한 주차 안에서만 지정 가능)" : ""}
                                        {form.id
                                            ? " · 같은 부서에 이미 그 날짜 회의록이 있으면 저장할 수 없습니다"
                                            : dup
                                                ? " · ⚠ 이 날짜에 이미 회의록이 있습니다 — 저장하면 기존 내용이 대체됩니다"
                                                : " · 같은 날짜에 이미 회의록이 있으면 그 회의록이 수정됩니다"}
                                    </Typography>
                                );
                            })()}
                        </FormField>
                        <FormField label="장소 (선택)">
                            <TextField
                                fullWidth size="small"
                                value={form.location || ""}
                                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                                placeholder="예: 본사 3층 회의실 / 온라인(Teams)"
                                inputProps={{ maxLength: 200 }}
                            />
                        </FormField>
                        <FormField label="참석자 (선택)">
                            <Stack spacing={0.75}>
                                <EmployeeAutocomplete
                                    multiple
                                    value={form.attendees || []}
                                    onChange={(v) => setForm((f) => (f ? { ...f, attendees: dedupeAttendees(v) } : f))}
                                    excludeIds={(form.attendees || []).map((a) => a?.id).filter((v) => v != null)}
                                    placeholder="이름·이메일로 참석자 검색"
                                    variant="outlined"
                                    disableUnderline={false}
                                />
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <ActionButton variant="secondary" startIcon={false} label="우리 부서 전원 추가"
                                        onClick={addMyDeptMembers}
                                        loading={deptAdding}
                                        disabled={!form.deptId}
                                        sx={{ fontSize: 12 }} />
                                    {(form.attendees || []).length > 0 && (
                                        <>
                                            <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                                                {form.attendees.length}명 선택됨
                                            </Typography>
                                            <ActionButton variant="secondary" startIcon={false} label="모두 지우기"
                                                onClick={() => setForm((f) => (f ? { ...f, attendees: [] } : f))}
                                                sx={{ fontSize: 12 }} />
                                        </>
                                    )}
                                </Stack>
                            </Stack>
                        </FormField>
                        <FormField label="업무 내용" required>
                            <TextField
                                fullWidth size="small" multiline minRows={14} maxRows={22}
                                value={form.workContent}
                                onChange={(e) => setForm((f) => ({ ...f, workContent: e.target.value }))}
                                placeholder="이번 회의에서 다룬 업무 내용을 입력하세요"
                            />
                        </FormField>
                        <FormField label="특이사항 (선택)">
                            <TextField
                                fullWidth size="small" multiline minRows={4} maxRows={8}
                                value={form.specialNotes}
                                onChange={(e) => setForm((f) => ({ ...f, specialNotes: e.target.value }))}
                                placeholder="특이사항이 있으면 입력하세요"
                            />
                        </FormField>
                        <FormField label="첨부파일 (선택)">
                            <input ref={fileInputRef} type="file" hidden multiple
                                onChange={(e) => {
                                    const fs = Array.from(e.target.files || []);
                                    setForm((f) => ({ ...f, pendingFiles: [...(f.pendingFiles || []), ...fs] }));
                                    e.target.value = "";
                                }} />
                            <Stack spacing={0.75}>
                                {(form.attachments || []).map((a) => (
                                    <Stack key={a.id} direction="row" alignItems="center" spacing={1}
                                        sx={{ border: `1px solid ${C.border}`, borderRadius: 1, px: 1, py: 0.5 }}>
                                        <InsertDriveFileRoundedIcon sx={{ fontSize: 16, color: C.textMuted, flexShrink: 0 }} />
                                        <Typography noWrap sx={{ flex: 1, fontSize: 12.5, color: C.textPrimary }}>{a.originalFileName}</Typography>
                                        <IconButton size="small" onClick={() => removeExistingAttachment(a.id)} title="삭제">
                                            <CancelRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                        </IconButton>
                                    </Stack>
                                ))}
                                {(form.pendingFiles || []).map((file, i) => (
                                    <Stack key={`p${i}`} direction="row" alignItems="center" spacing={1}
                                        sx={{ border: `1px dashed ${C.border}`, borderRadius: 1, px: 1, py: 0.5, bgcolor: "#f8fafc" }}>
                                        <InsertDriveFileRoundedIcon sx={{ fontSize: 16, color: C.primary, flexShrink: 0 }} />
                                        <Typography noWrap sx={{ flex: 1, fontSize: 12.5, color: C.textPrimary }}>{file.name}</Typography>
                                        <IconButton size="small" title="제거"
                                            onClick={() => setForm((f) => ({ ...f, pendingFiles: f.pendingFiles.filter((_, j) => j !== i) }))}>
                                            <CancelRoundedIcon sx={{ fontSize: 15, color: C.textMuted }} />
                                        </IconButton>
                                    </Stack>
                                ))}
                                <Box>
                                    <ActionButton variant="secondary" startIcon={false} label="파일 추가"
                                        onClick={() => fileInputRef.current?.click()} sx={{ fontSize: 12 }} />
                                </Box>
                            </Stack>
                        </FormField>
                    </Stack>
                )}
            </FormModal>
        </Box>
    );
}
